import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import {
  X,
  RefreshCw,
  Search,
  GripVertical,
  Pin,
  PinOff,
  Eye,
  EyeOff,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import { FilterBuilder } from "../filters";
import useAuthStore from "../../Zustand/Store/useAuthStore";

function formatPreviewCellValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (React.isValidElement(value)) return "";
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
}

/** Extracts a row array from common API response shapes (wrapper keys, paginators, direct array). */
function extractDataArrayFromResponse(responseData) {
  if (responseData == null || typeof responseData !== "object") return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.data)) return responseData.data;
  for (const value of Object.values(responseData)) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.data)) return value.data;
  }
  return [];
}

function estimateColumnWidth(label, minWidth = 120, maxWidth = 320) {
  const text = String(label || "").trim();
  const estimated = text.length * 9 + 48;
  return Math.min(maxWidth, Math.max(minWidth, estimated));
}

function withReadableColumnWidth(column) {
  if (!column) return column;
  const width = Math.max(
    Number(column.width) || 0,
    estimateColumnWidth(column.label || column.key)
  );
  return { ...column, width };
}

const PersonalizeDataGrid = ({
  dataApi,
  data: propData,
  contextId,
  tableName,
  displayTitle,
  filterModule,
  datagridKey: datagridKeyProp,
  defaultColumns: defaultColumnsProp,
  orgId: propOrgId,
  organizationUserId,
  userData: propUserData,
}) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { userData: storeUserData } = useAuthStore();
  
  // Use prop userData if provided, otherwise fall back to store
  const userData = propUserData || storeUserData;
  
  // Use prop orgId if provided, otherwise derive from userData
  const orgId = propOrgId || userData?.organization?.organization_id;

  // Consolidated state
  const [columns, setColumns] = useState([]);
  const [defaultColumns, setDefaultColumns] = useState([]); // Single source of truth for defaults
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [autoSaveState, setAutoSaveState] = useState("idle");
  const [configSource, setConfigSource] = useState(null);
  const [filters, setFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Refs
  const saveTimeout = useRef(null);
  const isInitialLoad = useRef(true);
  const isMountedRef = useRef(true);
  const orgEntityId = userData?.organization_entity_id || null;

  // Map columns with metadata
  const mapColumnsWithMeta = useCallback(
    (cols = []) =>
      cols.map((col, index) =>
        withReadableColumnWidth({
          ...col,
          key: col.key || col.field,
          required: col.mandatory || col.required || false,
          order: col.order ?? index,
          pinned: col.pinned === "left" ? "left" : undefined,
        })
      ),
    []
  );

  // Generate columns from data
  const generateColumnsFromData = useCallback((dataArray) => {
    if (!dataArray?.length) return [];

    const sampleRow = dataArray[0];
    const keys = Object.keys(sampleRow);

    return keys.map((key, index) =>
      withReadableColumnWidth({
        key,
        label: key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        visible: index <= 4 && index !== 0,
        pinned: undefined,
        width: 150,
        required: index === 1,
      })
    );
  }, []);

  // Load layout from backend
  const loadLayoutFromBackend = useCallback(async () => {
    if (!orgId || !(datagridKeyProp || tableName)) {
      console.warn("Missing orgId or datagridKey/tableName");
      setIsLoadingConfig(false);
      return null;
    }

    const datagridKey = datagridKeyProp
      ? `${datagridKeyProp}_${orgId}`
      : `${tableName}_grid_${orgId}`;

    try {
      const requestPayload = {
        datagrid_key: datagridKey,
        organization_id: orgId,
      };

      if (organizationUserId) {
        requestPayload.organization_user_id = organizationUserId;
      }

      const response = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(requestPayload),
      });

      if (response.ok) {
        const responseData = await response.json();
        
        if (responseData.datagrid?.datagrid_default_configuration) {
          const config = responseData.datagrid.datagrid_default_configuration;
          setConfigSource(responseData.type);
          console.log("✓ Loaded backend config:", config.columns?.length, "columns");
          return config;
        }
      } else if (response.status === 404) {
        console.log("No saved layout found, using defaults");
      }

      setConfigSource(null);
      return null;
    } catch (error) {
      console.error("Error loading layout:", error);
      setConfigSource(null);
      return null;
    } finally {
      setIsLoadingConfig(false);
    }
  }, [orgId, tableName, datagridKeyProp, token, organizationUserId]);

  // Build layout data for saving
  const buildLayoutData = useCallback(
    (columnSnapshot, filtersSnapshot = []) => ({
      columns: columnSnapshot.map((col, index) => ({
        key: col.key,
        label: col.label,
        width: col.width,
        visible: col.visible,
        pinned: col.pinned || undefined, // Use undefined instead of false
        order: index,
        required: col.required,
      })),
      filters: filtersSnapshot || [],
      timestamp: new Date().toISOString(),
    }),
    []
  );

  // Save layout to backend
  const saveLayoutToBackend = useCallback(
    async (layoutData) => {
      if (!orgId || !isMountedRef.current) {
        console.error("Cannot save: missing orgId or unmounted");
        return false;
      }

      const payload = {
        organization_id: orgId,
        organization_entity_id: orgEntityId,
        datagrid_key: datagridKeyProp
          ? `${datagridKeyProp}_${orgId}`
          : `${tableName}_grid_${orgId}`,
        datagrid_default_configuration: layoutData,
      };

      if (organizationUserId) {
        payload.organization_user_id = organizationUserId;
      }

      try {
        const response = await fetch(`${MAIN_URL}/api/organization-datagrids`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Save failed: ${response.status}`);
        }

        console.log("✓ Layout saved");
        if (isMountedRef.current) {
          setAutoSaveState("saved");
          setConfigSource(organizationUserId ? "organization_user" : "organization");
        }

        return true;
      } catch (error) {
        console.error("Save error:", error);
        setAutoSaveState("error");
        throw error;
      }
    },
    [orgId, orgEntityId, tableName, datagridKeyProp, token, organizationUserId]
  );

  // Reset layout
  const resetLayoutToDefault = useCallback(async () => {
    if (!orgId) {
      console.error("Cannot reset: missing orgId");
      return;
    }

    try {
      const datagridKey = datagridKeyProp
        ? `${datagridKeyProp}_${orgId}`
        : `${tableName}_grid_${orgId}`;
      const params = new URLSearchParams({
        datagrid_key: datagridKey,
        organization_id: orgId.toString(),
      });

      if (organizationUserId && configSource === "organization_user") {
        params.append("organization_user_id", organizationUserId.toString());
      }

      const response = await fetch(
        `${MAIN_URL}/api/datagrid/get-by-context?${params.toString()}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.status}`);
      }

      console.log("✓ Layout reset");
      
      // Reset to default columns
      const resetColumns = mapColumnsWithMeta(defaultColumns);
      setColumns(resetColumns);
      // Reset filters as well
      setFilters([]);
      setAutoSaveState("saved");
      
      // Reload from backend
      isInitialLoad.current = true;
      await loadLayoutFromBackend();

      return true;
    } catch (error) {
      console.error("Reset error:", error);
      setAutoSaveState("error");
      throw error;
    }
  }, [
    orgId,
    token,
    tableName,
    datagridKeyProp,
    organizationUserId,
    configSource,
    defaultColumns,
    loadLayoutFromBackend,
    mapColumnsWithMeta,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  const applyDefaultColumns = useCallback(
    (rows) => {
      if (defaultColumnsProp?.length) {
        setDefaultColumns(
          mapColumnsWithMeta(
            defaultColumnsProp.map((col, index) => ({
              ...col,
              key: col.key || col.field,
              order: col.order ?? index,
            }))
          )
        );
        return;
      }

      if (rows?.length > 0) {
        setDefaultColumns(generateColumnsFromData(rows));
      }
    },
    [defaultColumnsProp, generateColumnsFromData, mapColumnsWithMeta]
  );

  // Fetch data from API or use parent-provided rows (mapped for display keys)
  useEffect(() => {
    const fetchData = async () => {
      if (propData !== undefined && propData !== null) {
        const rows = Array.isArray(propData) ? propData : [];
        setData(rows);
        applyDefaultColumns(rows);
        return;
      }

      if (defaultColumnsProp?.length) {
        applyDefaultColumns([]);
      }

      const apiUrl = dataApi;

      // Fetch from API when dataApi is provided
      if (apiUrl) {
        setLoading(true);
        setError(null);

        try {
          console.log("Fetching from API:", apiUrl);
          const response = await axios.get(apiUrl, {
            params: contextId ? { context: contextId } : {},
            headers: getApiHeaders(),
          });

          // Check if API returned an error
          if (response.data.success === false) {
            console.warn("API returned success: false", response.data);
            // Still try to extract data if available, even if success is false
            if (response.data.data && Array.isArray(response.data.data)) {
              setData(response.data.data);
              const generatedColumns = generateColumnsFromData(response.data.data);
              setDefaultColumns(generatedColumns);
            } else {
              // No data available, but still allow personalization with saved layout
              setData([]);
              setDefaultColumns([]);
            }
            return;
          }

          // Extract data array from any common API response shape (no endpoint-specific logic)
          const fetchedData = extractDataArrayFromResponse(response.data);

          console.log("✓ Fetched:", fetchedData.length, "records", "Response:", response.data);
          setData(fetchedData);
          applyDefaultColumns(fetchedData);
        } catch (err) {
          setError(err.message || "Failed to fetch data");
          console.error("Fetch error:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [
    dataApi,
    propData,
    contextId,
    token,
    orgId,
    tableName,
    defaultColumnsProp,
    applyDefaultColumns,
  ]);

  // Initialize layout: load backend config and apply
  useEffect(() => {
    const initializeLayout = async () => {
      const columnsToUse = defaultColumns;

      if (columnsToUse.length === 0) {
        // Still load backend config (might have saved layout even with no data)
        const config = await loadLayoutFromBackend();
        if (config?.columns?.length > 0) {
          console.log("Applying backend config (no default columns)");
          setColumns(mapColumnsWithMeta(config.columns));
          setIsLoadingConfig(false);
          setFiltersLoaded(true);
        } else {
          // No default columns and no config, we're done loading
          setIsLoadingConfig(false);
          setFiltersLoaded(true);
        }
        return;
      }

      console.log("Initializing layout...");
      const config = await loadLayoutFromBackend();

      if (config?.columns?.length > 0) {
        console.log("Applying backend config");
        setColumns(mapColumnsWithMeta(config.columns));
        
        // Load filters if they exist in the config
        if (config.filters && Array.isArray(config.filters) && config.filters.length > 0) {
          console.log("Loading saved filters:", config.filters.length);
          setFilters(config.filters);
        }
      } else {
        console.log("Using default columns");
        setColumns(mapColumnsWithMeta(columnsToUse));
      }
      
      setIsLoadingConfig(false);
      // Mark filters as loaded (even if empty) so FilterBuilder knows it can initialize
      setFiltersLoaded(true);
    };

    initializeLayout();
  }, [defaultColumns, loadLayoutFromBackend, mapColumnsWithMeta, tableName]);

  // Auto-save when columns change (debounced)
  useEffect(() => {
    if (columns.length === 0 || isLoadingConfig) {
      return;
    }

    // Skip initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    // Clear existing timeout
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    // Debounced save
    saveTimeout.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      const layoutData = buildLayoutData(columns, filters);
      console.log("Auto-saving...");
      setAutoSaveState("saving");

      saveLayoutToBackend(layoutData).catch((error) => {
        console.error("Auto-save failed:", error);
        setAutoSaveState("error");
      });
    }, 700);

    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [columns, filters, isLoadingConfig, saveLayoutToBackend, buildLayoutData]);

  // Computed values
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columns;
    const query = searchQuery.toLowerCase();
    return columns.filter(
      (col) =>
        col.label.toLowerCase().includes(query) ||
        col.key.toLowerCase().includes(query)
    );
  }, [columns, searchQuery]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );

  const hiddenColumns = useMemo(
    () => columns.filter((col) => !col.visible),
    [columns]
  );

  const allNonMandatoryVisible = useMemo(() => {
    const nonMandatory = columns.filter((col) => !col.required);
    return nonMandatory.length > 0 && nonMandatory.every((col) => col.visible);
  }, [columns]);

  const noNonMandatoryVisible = useMemo(() => {
    const nonMandatory = columns.filter((col) => !col.required);
    return nonMandatory.length > 0 && nonMandatory.every((col) => !col.visible);
  }, [columns]);

  // Event handlers
  const toggleColumnVisibility = useCallback((key) => {
    setColumns((prev) =>
      prev.map((col) => {
        if (col.key !== key || col.required) return col;
        const nextVisible = !col.visible;
        if (!nextVisible) {
          return { ...col, visible: false };
        }
        return withReadableColumnWidth({ ...col, visible: true });
      })
    );
  }, []);

  // FIXED: Simplified pin toggle logic
  const toggleColumnPin = useCallback((key) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.key === key
          ? { ...col, pinned: col.pinned === "left" ? undefined : "left" }
          : col
      )
    );
  }, []);

  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setColumns((prev) => {
      const newColumns = [...prev];
      const draggedColumn = newColumns[draggedIndex];
      newColumns.splice(draggedIndex, 1);
      newColumns.splice(index, 0, draggedColumn);
      return newColumns;
    });
    setDraggedIndex(index);
  }, [draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const handleSelectAll = useCallback((event) => {
    const isChecked = event.target.checked;
    setColumns((prev) =>
      prev.map((col) => {
        const nextCol = {
          ...col,
          visible: col.required ? true : isChecked,
        };
        return nextCol.visible ? withReadableColumnWidth(nextCol) : nextCol;
      })
    );
  }, []);

  // Filter handlers
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // Filters will be auto-saved via the useEffect that watches filters state
  }, []);

  const handleApplyFilters = useCallback((appliedFilters) => {
    // Update filters state (auto-save will handle saving)
    setFilters(appliedFilters);
    console.log("Filters applied:", appliedFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters([]);
    setFilteredData(data);
    // Clearing filters will trigger auto-save via the existing useEffect
  }, [data]);

  // Loading states
  if (loading || isLoadingConfig) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Only show "No data available" when there is no data and no columns (e.g. no saved layout)
  if ((!Array.isArray(data) || data.length === 0) && columns.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please ensure there are records or a saved layout configuration.</Alert>
      </Box>
    );
  }

  const allVisible = allNonMandatoryVisible;
  const noneVisible = noNonMandatoryVisible;
  const toggleAll = () => handleSelectAll({ target: { checked: !allVisible } });

  return (
    <div className="ems-fade-up">
      {/* Mode banner */}
      <div className="ems-card ems-pv-mode">
        <div>
          <div className="ems-pv-mode-title">
            {(displayTitle || tableName) && (
              <span className="ems-pv-mode-tag">
                {displayTitle || tableName}
              </span>
            )}
            Personalization Mode
          </div>
          <div className="ems-pv-mode-sub">
            Personalize your grid layout. All changes are saved automatically.
          </div>
        </div>
        <button className="ems-btn ems-danger" onClick={() => navigate(-1)}>
          <X size={16} />
          Exit Personalization
        </button>
      </div>

      {/* Fields / Columns */}
      <div className="ems-card ems-pv-fields">
        <h2 className="ems-pv-h">Fields / Columns</h2>

        <div className="ems-pv-search">
          <Search size={17} />
          <input
            placeholder="Search columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="ems-pv-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <button className="ems-pv-selectall" type="button" onClick={toggleAll}>
          <span
            className={
              "ems-pv-check" +
              (allVisible ? " on" : !noneVisible ? " mixed" : "")
            }
          >
            {allVisible ? (
              <Check size={13} strokeWidth={3} />
            ) : (
              !noneVisible && <span className="ems-pv-dash" />
            )}
          </span>
          <span>Select All</span>
        </button>

        <div className="ems-pv-cols">
          {/* Visible */}
          <div>
            <div className="ems-pv-col-head visible">
              <Eye size={14} />
              Visible Columns
              <span className="ems-pv-col-n">{visibleColumns.length}</span>
            </div>
            <div className="ems-pv-list">
              {filteredColumns
                .filter((col) => col.visible)
                .map((col) => {
                  const index = columns.findIndex((c) => c.key === col.key);
                  return (
                    <div
                      key={col.key}
                      className="ems-pv-item"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="ems-pv-grip">
                        <GripVertical size={16} />
                      </span>
                      <span
                        className={"ems-pv-check" + (col.visible ? " on" : "")}
                        onClick={() =>
                          !col.required && toggleColumnVisibility(col.key)
                        }
                        style={col.required ? { opacity: 0.5, cursor: "default" } : undefined}
                      >
                        {col.visible && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="ems-pv-item-label">
                        {col.label}
                        {col.required && (
                          <span className="ems-pv-required">Required</span>
                        )}
                      </span>
                      <button
                        className={"ems-pv-act" + (col.pinned === "left" ? " on" : "")}
                        title={col.pinned === "left" ? "Unpin column" : "Pin column"}
                        onClick={() => toggleColumnPin(col.key)}
                      >
                        {col.pinned === "left" ? <Pin size={15} /> : <PinOff size={15} />}
                      </button>
                      <button
                        className="ems-pv-act"
                        title="Hide column"
                        disabled={col.required}
                        onClick={() => !col.required && toggleColumnVisibility(col.key)}
                      >
                        <EyeOff size={16} />
                      </button>
                    </div>
                  );
                })}
              {filteredColumns.filter((col) => col.visible).length === 0 && (
                <div className="ems-pv-empty">
                  {searchQuery ? "No visible columns match." : "No visible columns."}
                </div>
              )}
            </div>
          </div>

          {/* Hidden */}
          <div>
            <div className="ems-pv-col-head hidden">
              <EyeOff size={14} />
              Hidden Columns
              <span className="ems-pv-col-n">{hiddenColumns.length}</span>
            </div>
            <div className="ems-pv-list">
              {filteredColumns
                .filter((col) => !col.visible)
                .map((col) => (
                  <div
                    key={col.key}
                    className="ems-pv-item ghost"
                    onClick={() => toggleColumnVisibility(col.key)}
                  >
                    <span className="ems-pv-check" />
                    <span className="ems-pv-item-label">{col.label}</span>
                    <button
                      className="ems-pv-act"
                      title="Show column"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleColumnVisibility(col.key);
                      }}
                    >
                      <EyeOff size={16} />
                    </button>
                  </div>
                ))}
              {filteredColumns.filter((col) => !col.visible).length === 0 && (
                <div className="ems-pv-empty">
                  {searchQuery ? "No hidden columns match." : "All columns are visible."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="ems-card ems-pv-actions">
        <button className="ems-btn" onClick={resetLayoutToDefault}>
          <RefreshCw size={16} />
          Reset to Default
        </button>
        <div className="ems-pv-spacer" />
        <span
          className={
            "ems-pv-saved" +
            (autoSaveState === "saving" ? " saving" : "") +
            (autoSaveState === "error" ? " error" : "")
          }
        >
          {autoSaveState === "saving" ? (
            <>
              <span className="ems-pv-spin" />
              Saving changes…
            </>
          ) : autoSaveState === "error" ? (
            "Save failed"
          ) : autoSaveState === "saved" ? (
            <>
              <Check size={15} strokeWidth={2.6} />
              All changes saved
            </>
          ) : (
            ""
          )}
        </span>
      </div>

      {/* Preview */}
      <div className="ems-card ems-pv-preview">
        <div className="ems-pv-preview-head">
          <h2 className="ems-pv-h">Preview</h2>
          <span className="ems-pv-preview-note">
            {visibleColumns.length} columns · live preview of your grid
          </span>
        </div>
        <div className="ems-pv-table-wrap">
          <table className="ems-pv-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      position: col.pinned === "left" ? "sticky" : "static",
                      left: col.pinned === "left" ? 0 : undefined,
                      zIndex: col.pinned === "left" ? 2 : 1,
                      background: "var(--ems-surface-2)",
                      minWidth: col.width || estimateColumnWidth(col.label),
                      width: col.width || estimateColumnWidth(col.label),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {col.label}
                      {col.pinned === "left" && <Pin size={12} />}
                    </Box>
                  </th>
                ))}
                {visibleColumns.length === 0 && (
                  <th className="ems-pv-th-empty">No columns selected</th>
                )}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data) &&
                data.slice(0, 10).map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          position: col.pinned === "left" ? "sticky" : "static",
                          left: col.pinned === "left" ? 0 : undefined,
                          zIndex: col.pinned === "left" ? 2 : 1,
                          background: col.pinned === "left" ? "var(--ems-surface)" : "transparent",
                          minWidth: col.width || estimateColumnWidth(col.label),
                          width: col.width || estimateColumnWidth(col.label),
                        }}
                      >
                        {formatPreviewCellValue(row?.[col.key])}
                      </td>
                    ))}
                    {visibleColumns.length === 0 && (
                      <td className="ems-pv-td-empty">Enable columns above to preview the grid.</td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PersonalizeDataGrid;
                         
